<template>
  <dx-data-grid
      :ref="gridKey"
      :data-source="dataSource"
      :remote-operations="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :columns-auto-width="true"
      :word-wrap-enable="true"
      :row-alternation-enabled="true"
      :show-column-lines="true"
      :show-row-lines="true"
      :show-borders="true"
      :column-hiding-enabled="true"
      :selected-row-keys="selectedRowKeys"
      @selection-changed="onSelectionChanged"
      @toolbar-preparing="onToolbarPreparing($event)"
      @exporting="onExporting($event)"
      @initialized="saveGridInstance"
    >
      <dxSelection
        v-if="allowMultipleSelection"
        select-all-mode="allPages"
        show-check-boxes-mode="always"
        mode="multiple"
      />
      <dx-filter-panel :visible="allowFilter" />
      <dx-filter-row :visible="allowFilter" />
      <dx-header-filter :visible="false" />
      <dx-group-panel :visible="allowGrouping" />
      <dx-search-panel
        :visible="allowGeneralFilter === true ? true : allowFilter"
        :highlight-case-sensitive="true"
      />
      <dx-pager
        :allowed-page-sizes="pageSizes"
        :visible="true"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"

      />

      <dx-paging :page-size="pageSize" />
      <dx-state-storing
        :enabled="false"
        type="localStorage"
        :storage-key="gridKey"
      />

      <template v-for="column in columns">
        <dx-column
          v-bind:key="column.field"
          :data-field="column.field"
          :caption="column.caption"
          :data-type="column.type"
          :format="column.format || ''"
          :alignment="column.alignment || 'left'"
          :cell-template="column.hasTemplate ? 'customTemplate' : ''"
          :calculate-cell-value="column.calculateCellValue"
          :allow-sorting="column.allowSorting"
          :hiding-priority="0"
        />
      </template>

      <dx-column
        alignment="center"
        :allow-sorting="false"
        :allow-filtering="false"
        cell-template="commandsTemplate"
        :visible="hasActions && showCommands"
        :hiding-priority="1"
      />

      <div slot="commandsTemplate" slot-scope="row">
        <template v-for="action in actions">
          <v-tooltip top v-bind:key="action.name">
            <template v-slot:activator="{ on }">
              <a
                v-on="on"
                @click="actionClick($event, action.name, row.data.data)"
                class="pl-2 pr-2"
              >
                <v-icon :color="action.color">{{ action.icon }}</v-icon>
              </a>
            </template>
            <span>{{ action.tooltip }}</span>
          </v-tooltip>
        </template>
      </div>

      <template #addTemplate>
        <div>
          <v-btn small color="primary" @click.prevent="addClick">
            {{ $t("Add") }}
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>
      <template #extraActionsTemplate>
        <div>
          <slot name="action-buttons"></slot>
        </div>
      </template>
      <template #exportButtonTemplate>
        <div v-if="allowExport">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon color="green" @click.prevent="exportGrid" v-on="on">
                <v-icon large>mdi-microsoft-excel</v-icon>
              </v-btn>
            </template>
            <span>{{ `${$t("Export")} (.xlsx)` }}</span>
          </v-tooltip>
        </div>
      </template>

      <dx-column
        alignment="center"
        :allow-sorting="false"
        :allow-filtering="false"
        cell-template="customCommandsTemplate"
        :visible="!hasActions && showCommands"
      />

      <div slot="customCommandsTemplate" slot-scope="row">
        <slot name="customActionTemplates" :data="getRowData(row)"></slot>
      </div>

      <div slot="customTemplate" slot-scope="row">
        <slot name="columnTemplates" :data="getRowData(row)"></slot>
      </div>
    </dx-data-grid>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxFilterRow,
  DxHeaderFilter,
  DxFilterPanel,
  DxFilterBuilderPopup,
  DxStateStoring,
  DxGroupPanel,
  DxExport,
  DxButton,
  DxSelection,
  DxSummary,
} from "devextreme-vue/data-grid";

import * as frMessages from "devextreme/localization/messages/fr.json";
import * as deMessages from "devextreme/localization/messages/de.json";
import * as itMessages from "devextreme/localization/messages/it.json";
import * as ptMessages from "devextreme/localization/messages/pt.json";
import * as ruMessages from "devextreme/localization/messages/ru.json";
import * as esMessages from "devextreme/localization/messages/es.json";

import { locale, loadMessages } from "devextreme/localization";
import query from "devextreme/data/query";

export default {
  components: {
    //dx
    DxDataGrid,
    DxColumn,
    DxPager,
    DxPaging,
    DxSearchPanel,
    DxFilterRow,
    DxHeaderFilter,
    DxFilterPanel,
    DxFilterBuilderPopup,
    DxStateStoring,
    DxGroupPanel,
    DxExport,
    DxButton,
    DxSelection,
    DxSummary,
  },
  name: "DataGrid",
  props: {
    gridKey: {
      type: String,
      required: true,
    },
    dataSource: {
      type: [Object, Array],
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    actions: {
      type: Array,
      default: () => [],
    },
    allowMultipleSelection: {
      type: Boolean,
      default: false,
    },
    showCommands: {
      type: Boolean,
      default: true,
    },
    allowFilter: {
      type: Boolean,
      default: true,
    },
    allowGeneralFilter: {
        type: Boolean,
        default: false,
    },
    allowGrouping: {
      type: Boolean,
      default: true,
    },
    allowExport: {
      type: Boolean,
      default: true,
    },
    exportFileName: {
      type: String,
      default: "DataGrid",
    },
  },
  data() {
    return {
      pageSize: 15,
      pageSizes: [5, 10, 15, 20, 100],
      selectedRowKeys: [],
      selectedRowsData: [],
      dataGridInstance: null,
    };
  },
  created() {
    var currentCulture = this.$store.getters["account/user"].CultureCode;
    switch (currentCulture) {
      case "fr":
        loadMessages(frMessages.default);
        break;
      case "de":
        loadMessages(deMessages.default);
        break;
      case "it":
        loadMessages(itMessages.default);
        break;
      case "pt":
        loadMessages(ptMessages.default);
        break;
      case "ru":
        loadMessages(ruMessages.default);
        break;
      case "es":
        loadMessages(esMessages.default);
        break;
    }

    locale(currentCulture);
  },
  computed: {
    hasActions() {
      return this.actions.length > 0;
    },
  },
  methods: {
    saveGridInstance(e) {
      this.dataGridInstance = e.component;
    },

    getRowData(row) {
      return {
        item: row.data.data,
        field: row.data.column.dataField,
      };
    },
    onSelectionChanged({ selectedRowKeys, selectedRowsData }) {
      this.selectedRowsData = selectedRowsData;
      this.selectedRowKeys = selectedRowKeys;
    },
    onToolbarPreparing(e) {
      if (this.$listeners["on-add-click"]) {
        e.toolbarOptions.items.unshift(
          {
            location: "after",
            template: "extraActionsTemplate",
          },
          {
            location: "after",
            template: "addTemplate",
          },
          {
            location: "after",
            template: "exportButtonTemplate",
          }
        );
      } else {
        e.toolbarOptions.items.unshift(
          {
            location: "after",
            template: "extraActionsTemplate",
          },
          {
            location: "after",
            template: "exportButtonTemplate",
          }
        );
      }
    },
    refresh() {
      this.$refs[this.gridKey].instance.refresh();
    },
    exportGrid() {
      this.$refs[this.gridKey].instance.exportToExcel(false);
    },
    onExporting(e) {
      e.fileName = `Doc.ECM - ${this.exportFileName}`;
    },
    addClick() {
      this.$emit("on-add-click");
    },
    actionClick(e, name, data) {
      e.preventDefault();
      this.$emit("on-row-action-click", { name, data });
    },
  },
};
</script>